import React, { useMemo, useEffect, useState, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { Button, Tag } from 'antd';
import { EyeOutlined, EnvironmentOutlined } from '@ant-design/icons';

// Fix default marker icon issue with webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const COLORS = {
  red: '#ef4444',
  yellow: '#f59e0b',
  green: '#22c55e',
};

const STATUS_LABELS = {
  red: "Bo'sh (band emas)",
  yellow: 'Qisman band',
  green: "To'liq band",
};

const iconCache = {};
const getIcon = (color) => {
  if (!iconCache[color]) {
    iconCache[color] = L.divIcon({
      className: 'custom-marker',
      html: `<div style="
        width: 34px; height: 34px; border-radius: 50% 50% 50% 0;
        background: ${color}; transform: rotate(-45deg);
        display: flex; align-items: center; justify-content: center;
        box-shadow: 0 3px 10px ${color}66; border: 2.5px solid white;
      "><div style="
        transform: rotate(45deg); color: white; font-size: 16px; font-weight: bold;
        line-height: 1;
      ">${color === COLORS.green ? '&#10003;' : color === COLORS.yellow ? '&#9679;' : '&#10005;'}</div></div>`,
      iconSize: [34, 34],
      iconAnchor: [17, 34],
      popupAnchor: [0, -34],
    });
  }
  return iconCache[color];
};

const WEEK_DAYS = 7;

// Find the densest cluster center among markers
const getDensestCenter = (markers) => {
  if (markers.length === 0) return null;
  if (markers.length === 1) return markers[0].position;

  // Group markers by proximity (within ~0.01 deg ≈ 1km)
  const RADIUS = 0.01;
  let bestCenter = markers[0].position;
  let bestCount = 0;

  markers.forEach((m) => {
    let count = 0;
    markers.forEach((n) => {
      const dLat = Math.abs(m.position[0] - n.position[0]);
      const dLng = Math.abs(m.position[1] - n.position[1]);
      if (dLat <= RADIUS && dLng <= RADIUS) count++;
    });
    if (count > bestCount) {
      bestCount = count;
      bestCenter = m.position;
    }
  });

  // Return average of the cluster around bestCenter
  const cluster = markers.filter((m) => {
    const dLat = Math.abs(m.position[0] - bestCenter[0]);
    const dLng = Math.abs(m.position[1] - bestCenter[1]);
    return dLat <= RADIUS && dLng <= RADIUS;
  });

  return [
    cluster.reduce((s, m) => s + m.position[0], 0) / cluster.length,
    cluster.reduce((s, m) => s + m.position[1], 0) / cluster.length,
  ];
};

// Inner component to handle flyTo after map mounts
const FlyToCenter = ({ markers }) => {
  const map = useMap();
  const [hasFlewToUser, setHasFlewToUser] = useState(false);
  const [initialized, setInitialized] = useState(false);

  const flyToFallback = useCallback(() => {
    const densest = getDensestCenter(markers);
    if (densest) {
      map.flyTo(densest, 14, { duration: 1.5 });
    }
  }, [map, markers]);

  useEffect(() => {
    if (initialized) return;
    setInitialized(true);

    if (!navigator.geolocation) {
      flyToFallback();
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        map.flyTo([latitude, longitude], 14, { duration: 1.5 });
        setHasFlewToUser(true);
      },
      () => {
        // Geolocation denied or failed — fly to densest cluster
        flyToFallback();
      },
      { timeout: 5000, enableHighAccuracy: false }
    );
  }, [initialized, map, flyToFallback]);

  // If markers update after initial load and user geo wasn't used, re-center
  useEffect(() => {
    if (!hasFlewToUser && markers.length > 0 && initialized) {
      flyToFallback();
    }
  }, [markers, hasFlewToUser, initialized, flyToFallback]);

  return null;
};

const getAvailabilityStatus = (providerId, serviceIds, slots) => {
  const providerSlots = (slots || []).filter(
    (s) => s.provider_id === providerId || serviceIds.includes(s.service_id)
  );

  if (providerSlots.length === 0) {
    return 'red';
  }

  const uniqueDays = new Set(providerSlots.map((s) => Number(s.day)));

  if (uniqueDays.size >= WEEK_DAYS) {
    return 'green';
  }

  return 'yellow';
};

const MapSection = ({ providers, services, availabilitySlots, onMarkerClick }) => {
  const markers = useMemo(() => {
    const result = [];

    (providers || []).forEach((p) => {
      const lat = parseFloat(p.latitude);
      const lng = parseFloat(p.longitude);
      if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
        const provServiceIds = (services || [])
          .filter((s) => s.provider_id === p.id)
          .map((s) => s.id);
        const status = getAvailabilityStatus(p.id, provServiceIds, availabilitySlots);
        const color = COLORS[status];

        result.push({
          id: `provider-${p.id}`,
          type: 'provider',
          data: p,
          position: [lat, lng],
          name: p.name,
          address: p.address,
          icon: getIcon(color),
          status,
          color,
        });
      }
    });

    (services || []).forEach((s) => {
      const lat = parseFloat(s.lat);
      const lng = parseFloat(s.lon);
      if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
        const alreadyHasProvider = result.some(
          (m) => m.type === 'provider' && m.data.id === s.provider_id
        );
        if (!alreadyHasProvider) {
          const status = getAvailabilityStatus(null, [s.id], availabilitySlots);
          const color = COLORS[status];

          result.push({
            id: `service-${s.id}`,
            type: 'service',
            data: s,
            position: [lat, lng],
            name: s.title,
            address: `Narxi: ${Number(s.price || 0).toLocaleString()} UZS`,
            icon: getIcon(color),
            status,
            color,
          });
        }
      }
    });

    return result;
  }, [providers, services, availabilitySlots]);

  const counts = useMemo(() => {
    const c = { red: 0, yellow: 0, green: 0 };
    markers.forEach((m) => { c[m.status] = (c[m.status] || 0) + 1; });
    return c;
  }, [markers]);

  const center = markers.length > 0
    ? [
        markers.reduce((s, m) => s + m.position[0], 0) / markers.length,
        markers.reduce((s, m) => s + m.position[1], 0) / markers.length,
      ]
    : [41.3111, 69.2797];

  return (
    <div style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid #f0f0f0' }}>
      <MapContainer
        center={center}
        zoom={markers.length > 0 ? 13 : 12}
        style={{ height: '70vh', width: '100%' }}
        scrollWheelZoom={true}
      >
        <FlyToCenter markers={markers} />
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {markers.map((marker) => (
          <Marker
            key={marker.id}
            position={marker.position}
            icon={marker.icon}
          >
            <Popup>
              <div style={{ minWidth: 220, padding: '4px 0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                  <Tag
                    color={marker.status === 'green' ? 'success' : marker.status === 'yellow' ? 'warning' : 'error'}
                    style={{ margin: 0, fontWeight: 600 }}
                  >
                    {STATUS_LABELS[marker.status]}
                  </Tag>
                  <Tag color={marker.type === 'provider' ? 'purple' : 'cyan'} style={{ margin: 0 }}>
                    {marker.type === 'provider' ? 'Provayder' : 'Xizmat'}
                  </Tag>
                </div>
                <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>
                  {marker.name}
                </div>
                <div style={{ color: '#666', fontSize: 12, marginBottom: 4 }}>
                  <EnvironmentOutlined style={{ marginRight: 4 }} />
                  {marker.address || 'Manzil ko\'rsatilmagan'}
                </div>
                <div style={{
                  fontSize: 11, color: '#999', marginBottom: 10,
                  padding: '4px 8px', background: '#f9f9f9', borderRadius: 4,
                }}>
                  Haftalik bandlik: <strong style={{ color: marker.color }}>
                    {marker.status === 'green' ? '7/7 kun' : marker.status === 'yellow' ? 'Qisman' : '0/7 kun'}
                  </strong>
                </div>
                {marker.type === 'provider' && (
                  <Button
                    type="primary"
                    size="small"
                    icon={<EyeOutlined />}
                    onClick={() => onMarkerClick && onMarkerClick(marker.data)}
                    style={{ width: '100%', borderRadius: 6 }}
                  >
                    To'liq ma'lumot
                  </Button>
                )}
                {marker.type === 'service' && (
                  <Button
                    type="primary"
                    size="small"
                    icon={<EyeOutlined />}
                    onClick={() => {
                      const prov = (providers || []).find(p => p.id === marker.data.provider_id);
                      if (prov && onMarkerClick) {
                        onMarkerClick(prov);
                      }
                    }}
                    style={{ width: '100%', borderRadius: 6 }}
                  >
                    Provayder ma'lumotlari
                  </Button>
                )}
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
      <div style={{
        padding: '10px 16px',
        background: '#fafafa',
        display: 'flex',
        gap: 20,
        alignItems: 'center',
        fontSize: 13,
        color: '#555',
        flexWrap: 'wrap',
        borderTop: '1px solid #f0f0f0',
      }}>
        <span style={{ fontWeight: 600, color: '#333' }}>Bandlik holati:</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 12, height: 12, borderRadius: '50%', background: COLORS.green, display: 'inline-block', boxShadow: `0 0 4px ${COLORS.green}66` }} />
          To'liq band ({counts.green})
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 12, height: 12, borderRadius: '50%', background: COLORS.yellow, display: 'inline-block', boxShadow: `0 0 4px ${COLORS.yellow}66` }} />
          Qisman band ({counts.yellow})
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 12, height: 12, borderRadius: '50%', background: COLORS.red, display: 'inline-block', boxShadow: `0 0 4px ${COLORS.red}66` }} />
          Bo'sh ({counts.red})
        </span>
        <span style={{ marginLeft: 'auto', color: '#999' }}>
          Jami: {markers.length} ta marker
        </span>
      </div>
    </div>
  );
};

export default MapSection;
